import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShoppingBag,
  Clock,
  CheckCircle,
  Truck,
  Package,
  XCircle,
  User,
  Phone,
  MapPin,
  Calendar,
  CreditCard,
  Search,
  RefreshCw,
  Eye,
  Trash2,
} from "lucide-react";

const ESTADOS = [
  { id: "pendiente", label: "Pendiente", color: "amber", icon: Clock },
  { id: "confirmado", label: "Confirmado", color: "blue", icon: CheckCircle },
  { id: "en_preparacion", label: "En Preparación", color: "purple", icon: Package },
  { id: "enviado", label: "Enviado", color: "indigo", icon: Truck },
  { id: "entregado", label: "Entregado", color: "green", icon: CheckCircle },
  { id: "cancelado", label: "Cancelado", color: "red", icon: XCircle },
];

export default function AdminPedidos() {
  const [pedidos, setPedidos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [search, setSearch] = useState("");
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState("");
  const [notasAdmin, setNotasAdmin] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filtroEstado]);

  const fetchData = async () => {
    try {
      const params = filtroEstado !== "todos" ? { estado: filtroEstado } : {};
      const [pedidosRes, resumenRes] = await Promise.all([
        api.get("/pedidos", { params }),
        api.get("/pedidos/resumen"),
      ]);
      setPedidos(pedidosRes.data);
      setResumen(resumenRes.data);
    } catch (error) {
      console.error("Error fetching pedidos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEstado = async () => {
    if (!selectedPedido || !nuevoEstado) return;
    setProcessing(true);
    try {
      await api.put(`/pedidos/${selectedPedido.id}/estado`, {
        estado: nuevoEstado,
        notas_admin: notasAdmin,
      });
      toast.success("Estado actualizado");
      setDialogOpen(false);
      setNuevoEstado("");
      setNotasAdmin("");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar estado");
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este pedido?")) return;
    try {
      await api.delete(`/pedidos/${id}`);
      toast.success("Pedido eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const getEstadoInfo = (estado) => {
    return ESTADOS.find((e) => e.id === estado) || ESTADOS[0];
  };

  const filtered = pedidos.filter(
    (p) =>
      p.id?.toLowerCase().includes(search.toLowerCase()) ||
      p.cliente_nombre?.toLowerCase().includes(search.toLowerCase()) ||
      p.cliente_telefono?.includes(search)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  const pendientes = resumen?.por_estado?.pendiente || 0;

  return (
    <div className="space-y-6" data-testid="admin-pedidos">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Pedidos Online</h1>
          <p className="text-torta-muted">Gestiona los pedidos de la landing page</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <ShoppingBag className="w-8 h-8 text-torta-brown mb-3" />
          <p className="text-torta-muted text-sm">Total Pedidos</p>
          <p className="font-display text-2xl text-torta-dark">{resumen?.total_pedidos || 0}</p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <Clock className="w-8 h-8 text-amber-600 mb-3" />
          <p className="text-amber-600 text-sm">Pendientes</p>
          <p className="font-display text-2xl text-amber-700">{pendientes}</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
          <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
          <p className="text-green-600 text-sm">Entregados</p>
          <p className="font-display text-2xl text-green-700">
            {resumen?.por_estado?.entregado || 0}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <CreditCard className="w-8 h-8 text-blue-600 mb-3" />
          <p className="text-blue-600 text-sm">Ventas Entregadas</p>
          <p className="font-display text-2xl text-blue-700">
            {formatCurrency(resumen?.total_ventas_entregados)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-torta-muted" />
          <Input
            placeholder="Buscar por número, nombre o teléfono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filtroEstado} onValueChange={setFiltroEstado}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {ESTADOS.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Pedidos List */}
      <div className="space-y-4">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <ShoppingBag className="w-12 h-12 text-torta-muted mx-auto mb-4" />
            <p className="text-torta-muted">No hay pedidos</p>
          </div>
        ) : (
          filtered.map((pedido) => {
            const estadoInfo = getEstadoInfo(pedido.estado);
            const Icon = estadoInfo.icon;
            return (
              <div key={pedido.id} className="bg-white rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${estadoInfo.color}-100`}
                    >
                      <Icon className={`w-5 h-5 text-${estadoInfo.color}-600`} />
                    </div>
                    <div>
                      <p className="font-display text-lg text-torta-dark">
                        #{pedido.id?.substring(0, 8).toUpperCase()}
                      </p>
                      <p className="text-xs text-torta-muted">
                        {new Date(pedido.fecha).toLocaleString("es-CO")}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium bg-${estadoInfo.color}-100 text-${estadoInfo.color}-700`}
                  >
                    {estadoInfo.label}
                  </span>
                </div>

                {/* Cliente Info */}
                <div className="bg-torta-cream/50 rounded-xl p-4 mb-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-torta-brown" />
                      <span className="text-sm text-torta-dark">{pedido.cliente_nombre}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-torta-brown" />
                      <span className="text-sm text-torta-dark">{pedido.cliente_telefono}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-torta-brown" />
                      <span className="text-sm text-torta-dark">{pedido.cliente_direccion} - {pedido.cliente_barrio}</span>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-torta-dark mb-2">Productos:</p>
                  <div className="flex flex-wrap gap-2">
                    {pedido.items?.map((item, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 bg-torta-sand/50 px-3 py-1.5 rounded-full text-sm"
                      >
                        {item.cantidad}x {item.producto_nombre}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-torta-sand">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-torta-muted">
                      Subtotal: {formatCurrency(pedido.subtotal)}
                    </span>
                    {pedido.costo_domicilio > 0 && (
                      <span className="text-torta-muted">
                        Domicilio: {formatCurrency(pedido.costo_domicilio)}
                      </span>
                    )}
                    <span className="font-medium text-torta-dark">
                      Total: {formatCurrency(pedido.total)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedPedido(pedido);
                        setNuevoEstado(pedido.estado);
                        setNotasAdmin(pedido.notas_admin || "");
                        setDialogOpen(true);
                      }}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Gestionar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(pedido.id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Dialog Gestionar Pedido */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Gestionar Pedido #{selectedPedido?.id?.substring(0, 8).toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Cambiar Estado</label>
              <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ESTADOS.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Notas internas</label>
              <Textarea
                value={notasAdmin}
                onChange={(e) => setNotasAdmin(e.target.value)}
                placeholder="Notas sobre el pedido..."
                rows={3}
              />
            </div>

            {selectedPedido?.notas && (
              <div className="bg-torta-cream/50 rounded-xl p-3">
                <p className="text-xs text-torta-muted mb-1">Notas del cliente:</p>
                <p className="text-sm text-torta-dark">{selectedPedido.notas}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateEstado}
                disabled={processing}
                className="flex-1 bg-torta-brown hover:bg-torta-brown/90"
              >
                Guardar Cambios
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
